import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	HostListener,
	inject,
	input,
	model,
	output,
	signal,
} from '@angular/core';
import { ESigningMetaDetailsComponent } from './e-signing-meta-details/e-signing-meta-details.component';
import {
	ESigningFieldDetailsClickEvent,
	ESigningFieldDetailsComponent,
} from './e-signing-field-details/e-signing-field-details.component';

import {
	ESigningCanvasComponent,
	type ESigningBlockChange,
	type PushBlock,
} from './e-signing-canvas/e-signing-canvas.component';
import {
	ClientSigningDetailsViewModelComplete,
	ESigningBlock,
	ESigningBlockType,
	PdfPageDetail,
} from '@models/e-signing';
import { SelectOption } from 'src/app/ui/select/select.component';
import {
	CALL_TO_ACTION_TYPES,
	NOTE_TYPES,
} from 'src/app/ui/note/note.component';
import {
	ESigningViewTab,
	ESigningViewTabComponent,
} from '../envelope/e-signing-view-tab/e-signing-view-tab.component';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentToRenderPayload } from '../../../documents-v2/matter-packages/matter-package/matter-package-document-list/matter-package-document-list.component';
import { MatterPackagePreviewComponent } from '../../../documents-v2/matter-packages/matter-package/matter-package-preview/matter-package-preview.component';
import { ESigningDisclaimerComponent } from './e-signing-disclaimer/e-signing-disclaimer.component';
import { ESigningPackageDocumentListDetailsComponent } from './e-signing-package-document-list-details/e-signing-package-document-list-details.component';

@Component({
	selector: 'app-envelope-draft',
	imports: [
		ESigningMetaDetailsComponent,
		ESigningFieldDetailsComponent,
		ESigningCanvasComponent,
		ESigningViewTabComponent,
		ESigningDisclaimerComponent,
		ESigningPackageDocumentListDetailsComponent,
		MatterPackagePreviewComponent,
	],
	templateUrl: './envelope-draft.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'overflow-hidden h-full' },
})
export class EnvelopeDraftComponent {
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	public ESigningViewTab = ESigningViewTab;

	private readonly BLOCK_DIMENSIONS = {
		WIDTH: {
			[ESigningBlockType.SIGN]: 202,
			[ESigningBlockType.INITIALS]: 168,
			[ESigningBlockType.DATE]: 208,
			[ESigningBlockType.CHECKBOX]: 48,
			[ESigningBlockType.TEXT]: 312,
		},
		DEFAULT_WIDTH: 200,
		HEIGHT: 51,
	} as const;

	recipients_ = input.required<ClientSigningDetailsViewModelComplete[]>();
	pages_ = input.required<PdfPageDetail[]>();
	eSigningBlocks_ = input.required<Omit<ESigningBlock, 'UIVariant'>[]>();
	eSigningBlockChange = output<ESigningBlockChange>();
	envelopeId_ = input.required<string>();
	isSignatureDrawOnly_ = model.required<boolean>();
	editSigners_ = output<void>();
	tabChanged_ = output<{ from: ESigningViewTab; to: ESigningViewTab }>();
	isSnapshotting_ = input<boolean>(false);
	NOTE_CLOSE_TYPE = CALL_TO_ACTION_TYPES.CLOSE;
	NOTE_INFO_TYPE = NOTE_TYPES.INFO;

	public matterPackageViewModel_ = signal<DocumentToRenderPayload>({
		docxPreviewDocId: null,
		form: null,
		uploadedDocPreviewSourcePath: null,
	});

	public hasDocumentToRender_ = computed(() =>
		Object.values(this.matterPackageViewModel_())?.some(Boolean),
	);

	constructor() {
		let previousTab: ESigningViewTab | null = null;

		effect(() => {
			const tab = this.selectedTab_();
			if (this.isValidESigningViewTab(tab)) {
				this.router.navigate([], {
					relativeTo: this.activatedRoute,
					queryParams: { tab },
					queryParamsHandling: 'merge',
				});

				// Emit tab change event
				if (previousTab && previousTab !== tab) {
					this.tabChanged_.emit({ from: previousTab, to: tab });
				}

				previousTab = tab;
			}
		});
	}

	@HostListener('document:keydown.escape', ['$event'])
	onEscapeKey(event: KeyboardEvent) {
		let cleared = false;
		if (this.selectedBlock_()) {
			this.selectedBlock_.set(null);
			cleared = true;
		}
		if (this.pendingDragBlock_()) {
			this.pendingDragBlock_.set(null);
			cleared = true;
		}
		if (cleared) {
			event.preventDefault();
		}
	}

	private getBlockDimensions(blockType: ESigningBlockType): {
		width: number;
		height: number;
	} {
		return {
			width:
				this.BLOCK_DIMENSIONS.WIDTH[blockType] ??
				this.BLOCK_DIMENSIONS.DEFAULT_WIDTH,
			height: this.BLOCK_DIMENSIONS.HEIGHT,
		};
	}

	selectedTab_ = signal<ESigningViewTab>(this.initTabSignal.bind(this)());

	options_ = computed<SelectOption[]>(() =>
		this.recipients_().map((recipient) => ({
			id: recipient.id,
			text: recipient.name,
			avatarInitials: recipient.initials,
		})),
	);

	avatars_ = computed(() =>
		this.recipients_().map((recipient) => ({
			initials: recipient.initials || '',
			id: recipient.id,
			tooltipText: recipient.name,
		})),
	);
	selectedBlock_ = signal<ESigningFieldDetailsClickEvent>(null);
	pendingDragBlock_ = signal<PushBlock>(null);

	pendingBlock_ = computed<PushBlock>(() => {
		const selected = this.selectedBlock_();
		if (!selected) return null;

		const { width, height } = this.getBlockDimensions(selected.type);

		return {
			elementType: selected.type,
			clientId: selected.recipient.id,
			UIVariant: selected.variant,
			width,
			height,
		};
	});

	public onFieldDragStart(fieldDetails: ESigningFieldDetailsClickEvent): void {
		const { width, height } = this.getBlockDimensions(fieldDetails.type);

		const dragBlock = {
			elementType: fieldDetails.type,
			clientId: fieldDetails.recipient.id,
			UIVariant: fieldDetails.variant,
			width,
			height,
		};

		this.pendingDragBlock_.set(dragBlock);
	}

	public onBlockPlacedViaDrag(): void {
		this.selectedBlock_.set(null);
		this.pendingDragBlock_.set(null);
	}

	private isValidESigningViewTab(value: string): value is ESigningViewTab {
		return Object.values(ESigningViewTab).includes(value as ESigningViewTab);
	}

	private initTabSignal() {
		const tab = this.activatedRoute.snapshot.queryParamMap?.get('tab');
		return this.isValidESigningViewTab(tab) ? tab : ESigningViewTab.ESIGNING;
	}
}
